import { EditableSectionRow } from '../../../../EditableSection';
import { Box, IconButton, Typography } from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import { EmailForm } from './SettingsEmailForm/settings-email.form';
import { useFormikContext } from 'formik';
import { SettingsEmailFormType } from './EmailSettingsRow.hooks';
import { useCallback, useMemo } from 'react';
import { FileCopyOutlined } from '@mui/icons-material';
import { adminResendVerificationEmail } from '@uplift/api';
import { useAlert } from '../../../../AlertProvider';
import { ClickToCopy } from '../../../../ClickToCopy';
import { VerificationTag } from '../SettingsComponents';
import { UserRole } from '@uplift/types';

export type EmailSettingsRowProps = {
  email: string;
  rowKey: string;
  verified: boolean;
  showIcon?: boolean;
  role: UserRole;
  userId: string;
};


export const EmailSettingsRow: React.FC<EmailSettingsRowProps> = ({ email, rowKey, verified, showIcon, role, userId }) => {
  const { classes } = useEmailStyles();
  const snackbar = useAlert();
  const formik = useFormikContext<SettingsEmailFormType>();

  const isDisabled = useMemo(() => {
    if (formik?.values?.email === formik?.initialValues?.email) {
      return true;
    }
    if (formik?.values?.verifiableInfo?.length < 3) {
      return true;
    }
    return false;
  }, [ formik?.initialValues.email, formik?.values.email, formik?.values.verifiableInfo?.length ]);

  const handleRequestNewLink = useCallback(async () => {
    await adminResendVerificationEmail({ userId });

    snackbar.success('Verification Email Sent');
  }, [ snackbar, userId ]);

  return (
    <EditableSectionRow
      rowKey={rowKey}
      label="Account email"
      form={<EmailForm role={role} />}
      kind="editable-row"
      rowFormWrapperProps={{ saveButtonProps: { disabled: isDisabled },
      }}
    >
      <Box>
        <Box className={classes.root}>
          <Box className={classes.label}>
            <Box pr={1}>
              <Typography style={{ fontWeight: 500 }}>{email}</Typography>
            </Box>
            {showIcon && (
              <Box pr={1}>
                <ClickToCopy text={email}>
                  <IconButton size="small" data-testid="click-to-copy-button">
                    <FileCopyOutlined fontSize="inherit" />
                  </IconButton>
                </ClickToCopy>
              </Box>
            )}
          </Box>
          <VerificationTag verified={verified}/>
        </Box>
        {!verified && (
          <Box onClick={handleRequestNewLink} pt={1}>
            <Typography color="textSecondary" className={classes.link}>Resend verification email</Typography>
          </Box>
        )}
      </Box>
    </EditableSectionRow>
  );
};

const useEmailStyles = makeStyles({ name: 'Email' })(theme => ({
  root: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    // gridGap: theme.spacing(1)
  },
  label: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    paddingRight: theme.spacing(1)
  },
  verification: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    gridGap: theme.spacing(.5),
  },
  link: {
    cursor: 'default',
    '&:hover': {
      textDecoration: 'underline',
      cursor: 'pointer'
    }
  }
}));
